/* ==========================================================================
   ECOFUEGO — Landing Page behaviour  ·  ecofuego-landing.js
   --------------------------------------------------------------------------
   Two jobs, both small:

   1. Multi-line add to cart for the offer tiers. A tier is a set of line
      items, not a bundle SKU, so the button posts every line in ONE
      /cart/add.js request. The tier discount is not applied here — Kaching
      Bundles keys its rules off cart quantity, so getting the right lines
      into the cart is what triggers the price.

   2. Reserve the height of the fixed CTA bar at the foot of the document so
      it never covers the last section.

   3. Play ambient video only while it is on screen. This page carries ten
      video elements; letting every autoplay clip decode at once is what
      turns a landing page into a battery complaint on the phones that make
      up three quarters of this store's traffic. Evidence video is
      user-initiated and is never touched by this.

   Loaded only on lp-* templates. Everything is namespaced and nothing here
   touches app-injected DOM.
   ========================================================================== */
(function () {
  'use strict';

  // Both the offer section and the sticky bar pull this file in, so that a
  // section still works if it is dropped onto a page on its own. The browser
  // fetches it once but executes it per tag, so guard the whole thing —
  // otherwise every scroll listener is bound twice.
  if (window.__efLpBooted) return;
  window.__efLpBooted = true;

  /* ── 1 · Tier add to cart ────────────────────────────────────────────── */

  function parseLines(spec) {
    // "variantId:qty,variantId:qty"
    return spec
      .split(',')
      .map(function (pair) {
        var bits = pair.split(':');
        var id = parseInt(bits[0], 10);
        var qty = parseInt(bits[1], 10);
        if (!id || !qty || qty < 1) return null;
        return { id: id, quantity: qty };
      })
      .filter(Boolean);
  }

  function routeAfterAdd(mode) {
    if (mode === 'checkout') {
      window.location.href = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'checkout'
        : '/checkout';
      return;
    }
    if (mode === 'drawer') {
      // Let the cart drawer / Kaching cart pick the change up on its own.
      document.dispatchEvent(new CustomEvent('ef-lp:cart-updated'));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return;
    }
    window.location.href = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart'
      : '/cart';
  }

  function addTier(button) {
    var items = parseLines(button.getAttribute('data-ef-lp-add') || '');
    if (!items.length) return;

    var after = button.getAttribute('data-ef-lp-after') || 'cart';
    var label = button.getAttribute('data-ef-lp-label') || button.textContent;

    if (button.dataset.efLpBusy === '1') return;
    button.dataset.efLpBusy = '1';
    button.textContent = 'Adding…';
    button.setAttribute('aria-busy', 'true');

    fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (e) { throw e; });
        return res.json();
      })
      .then(function () {
        routeAfterAdd(after);
        // Only reached when we stay on the page.
        button.textContent = 'Added';
        window.setTimeout(function () {
          button.textContent = label;
          button.dataset.efLpBusy = '0';
          button.removeAttribute('aria-busy');
        }, 1800);
      })
      .catch(function (err) {
        // Most often a sold-out variant. Say so rather than failing silently.
        button.textContent = (err && err.description) ? 'Unavailable' : 'Try again';
        button.dataset.efLpBusy = '0';
        button.removeAttribute('aria-busy');
        window.setTimeout(function () { button.textContent = label; }, 2600);
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-ef-lp-add]');
    if (!button) return;
    event.preventDefault();
    addTier(button);
  });

  /* ── 2 · Reserve the fixed CTA bar's height ──────────────────────────── */

  function sizeStickyBar() {
    var bar = document.querySelector('.ef-lp-sticky');
    if (!bar) return;
    document.body.classList.add('ef-lp-has-sticky');
    document.documentElement.style.setProperty('--ef-lp-sticky-h', bar.offsetHeight + 'px');
  }

  /* ── 3 · Play ambient video only while it is on screen ───────────────── */

  var ticking = false;

  /*
    Deliberately NOT IntersectionObserver. Its first callback can fire before
    the media has laid out, and a zero-height video reads as
    not-intersecting — which paused the hero on arrival and then never fired
    again, because nothing about its position subsequently changed. The hero
    not moving is the one failure this page cannot afford.

    A scroll-driven check re-evaluates on every frame that matters, so a bad
    first measurement corrects itself immediately.
  */
  function syncAmbientVideo() {
    ticking = false;
    var videos = document.querySelectorAll('.ef-lp video[autoplay]');
    if (!videos.length) return;

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var margin = 300;
    var vh = window.innerHeight;

    Array.prototype.forEach.call(videos, function (v) {
      if (reduced) {
        if (!v.paused) v.pause();
        return;
      }
      var r = v.getBoundingClientRect();
      // A zero-height box means layout hasn't happened yet — leave it alone
      // rather than acting on a measurement we know to be wrong.
      if (r.height === 0) return;
      var near = r.bottom > -margin && r.top < vh + margin;
      if (near && v.paused) {
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      } else if (!near && !v.paused) {
        v.pause();
      }
    });
  }

  function queueSync() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(syncAmbientVideo);
  }

  window.addEventListener('scroll', queueSync, { passive: true });
  window.addEventListener('resize', queueSync);
  window.addEventListener('load', queueSync);

  function init() {
    sizeStickyBar();
    queueSync();
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
  window.addEventListener('resize', sizeStickyBar);

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', init);
})();
